import { selectLoggedinUserName } from 'accessors'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import TextInputWithEmoji from 'components/ReusableComponents/ManageHotel/TextInputWithEmoji'
import SavibookHeader from 'components/ReusableComponents/Savibook/Header'
import SavibookHero from 'components/ReusableComponents/Savibook/Hero'
import RibbonBanner from 'components/ReusableComponents/Savibook/RibbonBanner'
import Toggle from 'components/ReusableComponents/Toggle'
import PromoUsageWarningDialog from 'features/ManageCampaigns/EditPromoDialog/PromoUsageWarningDialog'
import usePromoValidation from 'hooks/usePromoValidaiton'
import { parentChains } from 'main/hotelBrandsAndChains'
import { useEffect, useState } from 'react'
import DatePicker from 'react-datepicker'
import { useSelector } from 'react-redux'

import ConfirmationDialog from '../../../components/ReusableComponents/ConfirmationDialog'
import styles from './EditPromoDialog.module.scss'

const nightsToStayOptions = {
  1: '1 night',
  2: '2 nights',
  3: '3 nights'
}

export const EditPromo = ({
  promoCode,
  setPromoCode,
  minNights,
  setMinNights,
  maxDiscount,
  setMaxDiscount,
  promoTagline,
  setPromoTagline,
  hasPromoValidityDateControls = true,
  hasPromoValidityDate,
  setHasPromoValidityDate,
  promoValidityStartDate,
  setPromoValidityStartDate,
  promoValidityEndDate,
  setPromoValidityEndDate,
  promoToggleLabel = 'Use promotion'
}) => {
  const [isUsingDiscount, setIsUsingDiscount] = useState(!!promoCode)

  const userName = useSelector(selectLoggedinUserName)

  useEffect(() => {
    if (!isUsingDiscount) {
      setPromoCode(null)
      setMinNights(1)
      setMaxDiscount(0)
    }
  }, [isUsingDiscount]) // eslint-disable-line

  const parentChainCode = useSelector(s => s.hotelDetails.parentChainCode)

  function getWordRateCode() {
    if (parentChainCode === parentChains.ihg || parentChainCode === parentChains.choice) {
      return 'rate code'
    }

    if (parentChainCode === parentChains.wyndham) {
      return 'corporate ID '
    }

    return 'promo code'
  }

  const wordRateCode = getWordRateCode()

  function handlePercentChange(e) {
    const value = e.target.value

    if (value > 70) {
      setMaxDiscount(70)
    } else if (value < 0) {
      setMaxDiscount(0)
    } else {
      setMaxDiscount(value)
    }
  }

  const handleChangeValidityDates = dates => {
    const [start, end] = dates

    setPromoValidityStartDate(start)
    setPromoValidityEndDate(end)
  }

  const isPromoValid = isUsingDiscount
    ? !!promoCode?.match(/^[A-Za-z0-9]{1,10}$/gm)
    : true

  return (
    <>
      <div className={styles.wrapper}>
        <Toggle
          label={promoToggleLabel}
          className={styles.toggle}
          checked={isUsingDiscount}
          handleChange={e => setIsUsingDiscount(e.target.checked)}
        />
        <div
          className={
            isUsingDiscount ? styles.discountHolder : styles.discountHolderDisabled
          }
        >
          <div className={styles.halfInputRow}>
            <TextInput
              label={`Offer ${wordRateCode}`}
              className={styles.textInput}
              value={promoCode || ''}
              valid={isPromoValid}
              onChange={e => setPromoCode(e.target.value)}
            />
            {promoCode && !isPromoValid && (
              <p className={styles.validationError}>Enter a valid promo code</p>
            )}
          </div>
          <div className={styles.halfInputRow}>
            <Dropdown
              disabled={!promoCode}
              options={nightsToStayOptions}
              label="Minumum nights to stay"
              value={minNights}
              onChange={e => setMinNights(e.target.value)}
            />
          </div>
          <br />
          <div className={styles.halfInputRow}>
            <TextInput
              label="Offer discount percentage"
              value={maxDiscount ?? 0}
              type="number"
              className={styles.textInput}
              min={0}
              max={70}
              onChange={handlePercentChange}
              onBlur={e => (e.target.value === '' ? setMaxDiscount(0) : false)}
            />
          </div>
        </div>
        <div className={styles.inputRow}>
          <TextInputWithEmoji
            label="Offer tagline"
            className={styles.textInput}
            value={promoTagline}
            onValueChange={setPromoTagline}
            maxLength={20}
          />
        </div>
        <div
          style={{
            marginTop: 20,
            opacity: isUsingDiscount ? 1 : 0.3,
            pointerEvents: isUsingDiscount ? 'auto' : 'none'
          }}
        >
          {hasPromoValidityDateControls && (
            <>
              <Toggle
                label={`Offer is valid between specific dates`}
                className={styles.toggle}
                checked={hasPromoValidityDate}
                handleChange={e => setHasPromoValidityDate(e.target.checked)}
              />
              <div className={styles.fullInputRow}>
                <DatePicker
                  onChange={handleChangeValidityDates}
                  startDate={promoValidityStartDate}
                  endDate={promoValidityEndDate}
                  disabled={!hasPromoValidityDate}
                  selectsRange={true}
                  customInput={
                    <TextInput label="Valid date range" className={styles.textInput} />
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>
      <SavibookHero lightbg>
        <SavibookHeader text={`For ${userName}`} />
        <RibbonBanner
          topText={`${userName.replace(/ .*/, '')}, you unlocked`}
          mainText={promoTagline}
        />
      </SavibookHero>
    </>
  )
}

const EditPromoDialog = ({
  title,
  minNightsToStay = 1,
  maxDiscountPercentage = 15,
  promotionCode,
  handleCloseDialog,
  handleSave,
  promotionTagline = 'The best rate',
  ...props
}) => {
  const [promoCode, setPromoCode] = useState(promotionCode)
  const [minNights, setMinNights] = useState(minNightsToStay)
  const [maxDiscount, setMaxDiscount] = useState(maxDiscountPercentage)
  const [promoTagline, setPromoTagline] = useState(promotionTagline)

  const [hasPromoValidityDate, setHasPromoValidityDate] = useState(
    props.hasPromoValidityDate
  )
  const [promoValidityStartDate, setPromoValidityStartDate] = useState(
    props.promoValidityStartDate ? new Date(props.promoValidityStartDate) : null
  )
  const [promoValidityEndDate, setPromoValidityEndDate] = useState(
    props.promoValidityEndDate ? new Date(props.promoValidityEndDate) : null
  )
  const [showPromoUsageWarning, setShowPromoUsageWarning] = useState(null)

  function saveChanges() {
    handleSave({
      promoCode,
      minNights,
      maxDiscount,
      promoTagline,
      hasPromoValidityDate,
      promoValidityStartDate,
      promoValidityEndDate
    })
  }

  const { checkRateCodeUsage } = usePromoValidation(promoCode)

  async function handleConfirmDialog() {
    const { messageType } = await checkRateCodeUsage(promoCode)

    if (!messageType) {
      saveChanges()
    }

    setShowPromoUsageWarning(messageType)
  }

  if (showPromoUsageWarning) {
    return (
      <PromoUsageWarningDialog
        softMessage={true}
        promoCode={promoCode}
        onClose={() => setShowPromoUsageWarning(false)}
        onConfirm={saveChanges}
        showPromoUsageWarning={showPromoUsageWarning}
      />
    )
  }

  return (
    <ConfirmationDialog
      title={title}
      positiveMessage="Save changes"
      negativeMessage="Cancel"
      onConfirm={handleConfirmDialog}
      onClose={() => handleCloseDialog()}
      isButtonDisabled={promoTagline.length === 0}
      contentPadding={0}
    >
      <EditPromo
        promoCode={promoCode}
        setPromoCode={setPromoCode}
        minNights={minNights}
        setMinNights={setMinNights}
        maxDiscount={maxDiscount}
        setMaxDiscount={setMaxDiscount}
        promoTagline={promoTagline}
        setPromoTagline={setPromoTagline}
        hasPromoValidityDate={hasPromoValidityDate}
        setHasPromoValidityDate={setHasPromoValidityDate}
        promoValidityStartDate={promoValidityStartDate}
        setPromoValidityStartDate={setPromoValidityStartDate}
        promoValidityEndDate={promoValidityEndDate}
        setPromoValidityEndDate={setPromoValidityEndDate}
      />
    </ConfirmationDialog>
  )
}

export default EditPromoDialog
